import { Component, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { take, tap } from 'rxjs';
import { Subscription } from 'rxjs';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { patternValidator, patternInvalidator } from '@core/validators/pattern.validator';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ResetPasswordComponent {
    resetPwdForm: FormGroup | any;
    resetFailed = false;
    passwordRequirements: string[] = [
    '- 8 characters in length',
    '- Contain at least one capital letter',
    '- Contain at least one lowercase letter',
    '- Contain at least one number',
    '- Contain at least 1 special character',
  ];
    guid: string = '';
    validGuid: boolean = false;
    private subscriptions = new Subscription();
    showPassword = false;
    showConfirmPassword = false;

    faEye = faEye;
    faEyeSlash = faEyeSlash;

    get newPassword() {
    return this.resetPwdForm.get('newPassword');
  }
    get confirmPassword() {
    return this.resetPwdForm.get('confirmPassword');
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private toastr: ToastrService
  ) {}

    ngOnInit(): void {
    this.guid = this.getGuidFromUrl();
    this.authService
      .isValidIdentifier(this.guid)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          this.toastr.error('Link is invalid');
          this.router.navigateByUrl('login');
        }
      });

    this.resetPwdForm = this.fb.group(
      {
        newPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            patternValidator(/\d/, { 'Missing Number': false }),
            patternValidator(/[A-Z]/, { 'Missing Uppercase character': false }),
            patternValidator(/[a-z]/, { 'Missing Lowercase character': false }),
            patternValidator(/[!@#\[\]\$%\^\*`~\-\=()_,.:><\\\/?]/, {
              'Missing Special Character': false,
            }),
            patternInvalidator(/[\+\&]/, {
              'Invalid Special Characters Used: &/+': false,
            }),
          ],
        ],
        confirmPassword: [null, [Validators.required]],
      },
      { validator: this.matchPassword('newPassword', 'confirmPassword') }
    );
  }

  resetPwd(): void {
    const payload = {
      passwordReset: this.confirmPassword.value,
      guid: this.guid,
    };
    this.authService
      .resetPassword(payload)
      .pipe(take(1))
      .subscribe((_res) => {
        this.toastr.success('Password reset successful!');
        this.resetPwdForm.reset();
        setTimeout(() => {
          this.router.navigateByUrl('/login');
        }, 3000);
      });
  }

    matchPassword(newPassword: string, confirmPassword: string) {
    return (resetPwdForm: FormGroup) => {
      const password = resetPwdForm.controls[newPassword];
      const confirmedPassword = resetPwdForm.controls[confirmPassword];
      if (password.value !== confirmedPassword.value) {
        return confirmedPassword.setErrors({ passwordMismatch: true });
      } else {
        return confirmedPassword.setErrors(null);
      }
    };
  }

    getGuidFromUrl(): string {
    const routerUrl = this.router.url;
    const indexOfSlash = routerUrl.lastIndexOf('/');
    return routerUrl.slice(indexOfSlash + 1);
  }

  togglePassword(type: string): void {
    if(type == 'password') this.showPassword = !this.showPassword;
    else this.showConfirmPassword = !this.showConfirmPassword;
  }

    onDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
